import { useMemo, useState } from 'react';
import {
  Briefcase,
  CreditCard,
  FileSignature,
  FileText,
  GraduationCap,
  ShieldCheck,
  User as UserIcon,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import { formatDayMonYear } from '../utils/formatDate';
import { isEmptyAddress, parseAddress } from '../utils/parseAddress';
import type { CaseAttachment, OnboardingCase } from '../types/onboarding';

/**
 * Read-only "after the candidate submitted" views shown on the case detail
 * page. All sections source their data directly from the case payload —
 * `GET /console/user_onboarding/list/:id` embeds bank, document details,
 * and grouped `attachments` (`identity` / `bank` / `education` /
 * `experience` / `agreement` / `kyc`), so we no longer fan out into
 * `existing_submission` per section. A section that has no data simply
 * hides itself.
 */

interface PanelProps {
  candidate: OnboardingCase;
}

export const CandidateDetailsPanel = ({ candidate }: PanelProps) => {
  return (
    <div className="space-y-6">
      <PersonalDetails candidate={candidate} />
      <BankDetails candidate={candidate} />
      <DocumentsSection candidate={candidate} />
      <EducationSection candidate={candidate} />
      <ExperienceSection candidate={candidate} />
      <AgreementsSection candidate={candidate} />
      <BackgroundVerificationSection candidate={candidate} />
    </div>
  );
};

// ── shared helpers ────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const Section = ({ title, icon, children }: SectionProps) => (
  <section className="card">
    <div className="card-header flex items-center gap-2">
      <span className="text-rgf-gray-500">{icon}</span>
      <h2 className="text-base font-semibold text-rgf-gray-900">{title}</h2>
    </div>
    <div className="card-body">{children}</div>
  </section>
);

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

const DetailRow = ({ label, value }: DetailRowProps) => (
  <div className="space-y-0.5">
    <p className="text-xs font-medium uppercase tracking-wide text-rgf-gray-500">{label}</p>
    <p className="text-sm text-rgf-gray-900 break-words">
      {value || <span className="italic text-rgf-gray-400">Not provided</span>}
    </p>
  </div>
);

const titleCase = (v: string): string => v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ── personal ──────────────────────────────────────────────────────────────

const PersonalDetails = ({ candidate }: PanelProps) => {
  // New API contract: `address` is a single comma-separated string at the
  // top of the case ("Village (CODE), Block, District, PIN"). Split into
  // four named pieces so each gets its own row — matches the spec the
  // export uses too, so what reviewers see on screen matches the CSV.
  const address = useMemo(() => parseAddress(candidate.address), [candidate.address]);
  const pan = candidate.document_details?.pan_card_number ?? null;
  const aadhaar = candidate.document_details?.aadhaar_number ?? candidate.document_details?.aadhaar_last4
    ? candidate.document_details?.aadhaar_number ??
      (candidate.document_details?.aadhaar_last4
        ? `•••• •••• ${candidate.document_details.aadhaar_last4}`
        : null)
    : null;

  const hasAny =
    candidate.date_of_birth ||
    candidate.joining_date ||
    candidate.gender ||
    candidate.marital_status ||
    candidate.father_or_husband_name ||
    candidate.rgf_id ||
    !isEmptyAddress(address) ||
    pan ||
    aadhaar;
  if (!hasAny) return null;

  return (
    <Section title="Personal Details" icon={<UserIcon className="h-4 w-4" />}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {candidate.rgf_id && <DetailRow label="RGF ID" value={candidate.rgf_id} />}
        {candidate.date_of_birth && <DetailRow label="Date of Birth" value={formatDayMonYear(candidate.date_of_birth)} />}
        {candidate.joining_date && <DetailRow label="Date of Joining" value={formatDayMonYear(candidate.joining_date)} />}
        {candidate.gender && <DetailRow label="Gender" value={candidate.gender} />}
        {candidate.marital_status && <DetailRow label="Marital Status" value={candidate.marital_status} />}
        {candidate.father_or_husband_name && (
          <DetailRow label="Father / Husband Name" value={candidate.father_or_husband_name} />
        )}
        {address.village && <DetailRow label="Village" value={address.village} />}
        {address.block && <DetailRow label="Block" value={address.block} />}
        {address.district && <DetailRow label="District" value={address.district} />}
        {address.pin && <DetailRow label="PIN" value={address.pin} />}
        {pan && <DetailRow label="PAN Card Number" value={pan} />}
        {aadhaar && <DetailRow label="Aadhaar Number" value={aadhaar} />}
      </div>
    </Section>
  );
};

// ── bank ──────────────────────────────────────────────────────────────────

const BankDetails = ({ candidate }: PanelProps) => {
  const b = candidate.bank_account;
  if (!b) return null;
  const account =
    b.account_number ??
    (b.account_number_last4 ? `•••• •••• ${b.account_number_last4}` : null);
  return (
    <Section title="Bank Details" icon={<CreditCard className="h-4 w-4" />}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {b.account_holder_name && <DetailRow label="Account Holder Name" value={b.account_holder_name} />}
        {b.name && <DetailRow label="Bank Name" value={b.name} />}
        {account && <DetailRow label="Account Number" value={account} />}
        {b.ifsc_code && <DetailRow label="IFSC Code" value={b.ifsc_code} />}
        {b.account_type && <DetailRow label="Account Type" value={b.account_type} />}
        {b.bank_address && <DetailRow label="Bank Address" value={b.bank_address} />}
      </div>
    </Section>
  );
};

// ── attachments list (shared between sections) ────────────────────────────

const flattenAttachments = (
  candidate: OnboardingCase,
  groups: readonly string[],
): CaseAttachment[] => {
  const all: CaseAttachment[] = [];
  const buckets = candidate.attachments ?? {};
  for (const g of groups) {
    const list = buckets[g];
    if (Array.isArray(list)) all.push(...list);
  }
  return all;
};

const AttachmentList = ({
  rows,
  caseId,
}: {
  rows: Array<{ key: string; label: string; subtitle?: string; versionId: number; title: string }>;
  caseId: number;
}) => {
  const [preview, setPreview] = useState<{ versionId: number; title: string } | null>(null);
  if (rows.length === 0) return null;
  return (
    <>
      <ul className="divide-y divide-rgf-gray-100">
        {rows.map((r) => (
          <li key={r.key} className="py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-rgf-gray-900">{r.label}</p>
              {r.subtitle && (
                <p className="text-xs text-rgf-gray-500 truncate" title={r.subtitle}>
                  {r.subtitle}
                </p>
              )}
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setPreview({ versionId: r.versionId, title: r.title })}
            >
              View
            </Button>
          </li>
        ))}
      </ul>
      <DocumentPreviewModal
        entityType="case"
        entityId={caseId}
        versionId={preview?.versionId ?? null}
        title={preview?.title}
        onClose={() => setPreview(null)}
      />
    </>
  );
};

// ── documents (identity + bank cheque) ────────────────────────────────────

const DocumentsSection = ({ candidate }: PanelProps) => {
  const rows = useMemo(() => {
    const docs = flattenAttachments(candidate, ['identity', 'bank']);
    return docs.map((a) => ({
      key: `${a.document_type_code}-${a.document_version_id}`,
      label: a.document_type_name || titleCase(a.document_type_code),
      subtitle: a.filename ?? undefined,
      versionId: a.document_version_id,
      title: a.document_type_name || titleCase(a.document_type_code),
    }));
  }, [candidate]);

  if (rows.length === 0) return null;
  return (
    <Section title="Documents" icon={<FileText className="h-4 w-4" />}>
      <AttachmentList rows={rows} caseId={candidate.id} />
    </Section>
  );
};

// ── education (10th/12th marksheet, graduation degree, etc.) ──────────────

const EducationSection = ({ candidate }: PanelProps) => {
  const rows = useMemo(() => {
    const docs = flattenAttachments(candidate, ['education']);
    return docs.map((a) => ({
      key: `${a.document_type_code}-${a.document_version_id}`,
      label: a.document_type_name || titleCase(a.document_type_code),
      subtitle: a.filename ?? undefined,
      versionId: a.document_version_id,
      title: a.document_type_name || titleCase(a.document_type_code),
    }));
  }, [candidate]);

  if (rows.length === 0) return null;
  return (
    <Section title="Education" icon={<GraduationCap className="h-4 w-4" />}>
      <AttachmentList rows={rows} caseId={candidate.id} />
    </Section>
  );
};

// ── experience (resume, work-history attachments) ─────────────────────────

const ExperienceSection = ({ candidate }: PanelProps) => {
  const rows = useMemo(() => {
    const docs = flattenAttachments(candidate, ['experience']);
    return docs.map((a) => ({
      key: `${a.document_type_code}-${a.document_version_id}`,
      label: a.document_type_name || titleCase(a.document_type_code),
      subtitle: a.filename ?? undefined,
      versionId: a.document_version_id,
      title: a.document_type_name || titleCase(a.document_type_code),
    }));
  }, [candidate]);

  if (rows.length === 0) return null;
  return (
    <Section title="Experience" icon={<Briefcase className="h-4 w-4" />}>
      <AttachmentList rows={rows} caseId={candidate.id} />
    </Section>
  );
};

// ── agreements (compliance ack + vendor form + collaboration agreement) ──

const AgreementsSection = ({ candidate }: PanelProps) => {
  const rows = useMemo(() => {
    const docs = flattenAttachments(candidate, ['agreement']);
    return docs.map((a) => ({
      key: `${a.document_type_code}-${a.document_version_id}`,
      label: a.document_type_name || titleCase(a.document_type_code),
      subtitle: a.filename ?? undefined,
      versionId: a.document_version_id,
      title: a.document_type_name || titleCase(a.document_type_code),
    }));
  }, [candidate]);

  if (rows.length === 0) return null;
  return (
    <Section title="Compliance & Agreements" icon={<FileSignature className="h-4 w-4" />}>
      <AttachmentList rows={rows} caseId={candidate.id} />
    </Section>
  );
};

// ── background verification ───────────────────────────────────────────────

const BackgroundVerificationSection = ({ candidate }: PanelProps) => {
  const rows = useMemo(() => {
    const docs = flattenAttachments(candidate, ['kyc']);
    return docs.map((a) => ({
      key: `${a.document_type_code}-${a.document_version_id}`,
      label: a.document_type_name || titleCase(a.document_type_code),
      subtitle: a.filename ?? undefined,
      versionId: a.document_version_id,
      title: a.document_type_name || titleCase(a.document_type_code),
    }));
  }, [candidate]);

  if (rows.length === 0) return null;
  return (
    <Section title="Background Verification" icon={<ShieldCheck className="h-4 w-4" />}>
      <AttachmentList rows={rows} caseId={candidate.id} />
    </Section>
  );
};
